/**
 * NotchesEntrance — public landing wizard at /n2.
 *
 * Implements the Understand → Evaluate → Execute progressive navigation
 * per docs/wiki/notches/public-site-docs/Notches_{Functional,Design}_Specification.md.
 *
 * URL state: `?path=<slug>/<slug>` on /n2. Empty query = Level 1. Direct
 * entry, browser Back/Forward, and refresh all restore the correct level.
 * Focus moves to the visible level's heading on navigation; on Back, focus
 * returns to the originating choice when the trail was walked in-app.
 *
 * Design tokens come from theme.ts (the existing /n2/* palette). No new
 * dependencies. Reduced-motion honored.
 *
 * This file replaces N2LandingPage as the /n2 route element. N2LandingPage
 * remains in the tree for reference/rollback; nothing routes to it today.
 */

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { palette, type as typeTokens } from './theme';
import {
  EntranceNode,
  entranceTree,
  resolvePath,
  trailToPath,
} from './entranceConfig';
import { getPlan, plansPagePath, type ResearchPlan } from './plansCatalog';
import { canAccessSymbol, type SymbolName } from '../lib/entitlements';

// Where each market's research board lives. The board itself enforces
// access; entries listed here are ONLY surfaced from BundleOverview to
// users who already pass canAccessSymbol, so we don't push public
// visitors into an auth-gated page they can't yet use.
const MARKET_BOARDS: Record<string, string> = {
  Premier: '/options-research',
  SPX: '/notches?symbol=SPX',
  SPY: '/notches?symbol=SPY',
  QQQ: '/notches?symbol=QQQ',
  IWM: '/notches?symbol=IWM',
  ES: '/notches?symbol=ES',
  MES: '/notches?symbol=MES',
  GC: '/notches?symbol=GC',
  CL: '/notches?symbol=CL',
  NQ: '/notches?symbol=NQ',
  MNQ: '/notches?symbol=MNQ',
};
const SYMBOL_MARKETS = new Set(['SPX', 'SPY', 'QQQ', 'IWM', 'ES', 'MES', 'GC', 'CL', 'NQ', 'MNQ']);

// One-time <link rel="stylesheet"> for the Fraunces display face. Matches the
// pattern used by N2Frame so /n2 pages share the font-load story.
function ensureFraunces() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('n2-fraunces-font')) return;
  const link = document.createElement('link');
  link.id = 'n2-fraunces-font';
  link.rel = 'stylesheet';
  link.href =
    'https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300..700;1,9..144,300..700&display=swap';
  document.head.appendChild(link);
}

const REDUCED_MOTION_MEDIA = '(prefers-reduced-motion: reduce)';

function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(REDUCED_MOTION_MEDIA).matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia(REDUCED_MOTION_MEDIA);
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return reduced;
}

// Simple responsive hook — width breakpoint for mobile stacked layout.
function useIsMobile(threshold = 640): boolean {
  const [mobile, setMobile] = useState<boolean>(() =>
    typeof window === 'undefined' ? false : window.innerWidth <= threshold
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setMobile(window.innerWidth <= threshold);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [threshold]);
  return mobile;
}

export const NotchesEntrance: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const reduced = useReducedMotion();
  const isMobile = useIsMobile();

  const pathParam = new URLSearchParams(location.search).get('path');
  const trail = useMemo(() => resolvePath(pathParam), [pathParam]);
  const level = trail.length; // 0 = root, 1 = Level 2 view, 2 = Level 3 view

  const headingRef = useRef<HTMLHeadingElement | null>(null);

  // Load font once.
  useEffect(() => {
    ensureFraunces();
  }, []);

  // Focus management: move focus to the visible heading when the level
  // changes. tabindex=-1 on the heading makes it programmatically focusable
  // without adding it to the tab order.
  useLayoutEffect(() => {
    if (headingRef.current) {
      headingRef.current.focus({ preventScroll: false });
    }
  }, [level, pathParam]);

  const goToPath = (newTrail: EntranceNode[]) => {
    const p = trailToPath(newTrail);
    navigate(p ? `/n2?path=${p}` : '/n2');
  };

  return (
    <div
      className="notches-entrance-root"
      style={{
        minHeight: '100svh',
        background: palette.paper,
        color: palette.ink,
        fontFamily: typeTokens.sans,
        // Reset CRA global center alignment
        textAlign: 'left',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <main
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: isMobile ? '32px 20px 24px' : '56px 32px 24px',
        }}
      >
        {level === 0 ? (
          <Level1
            headingRef={headingRef}
            reduced={reduced}
            isMobile={isMobile}
            onSelect={(node) => goToPath([node])}
          />
        ) : (
          <LevelBranch
            headingRef={headingRef}
            reduced={reduced}
            trail={trail}
            onSelect={(node) => goToPath([...trail, node])}
            onNavigateToTrail={goToPath}
          />
        )}
      </main>

      <UtilityFooter />
    </div>
  );
};

// ─── Level 1 — the three-circle entrance ─────────────────────────────────

const Level1: React.FC<{
  headingRef: React.RefObject<HTMLHeadingElement | null>;
  reduced: boolean;
  isMobile: boolean;
  onSelect: (node: EntranceNode) => void;
}> = ({ headingRef, reduced, isMobile, onSelect }) => {
  return (
    <div
      style={{
        width: '100%',
        maxWidth: 900,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: isMobile ? 32 : 48,
      }}
    >
      <BrandBlock reduced={reduced} />

      <h1
        ref={headingRef}
        tabIndex={-1}
        style={{
          fontFamily: typeTokens.serif,
          fontSize: isMobile ? 30 : 40,
          fontWeight: 400,
          margin: 0,
          textAlign: 'center',
          color: palette.ink,
          letterSpacing: '-0.01em',
          textWrap: 'balance' as any,
          outline: 'none',
          maxWidth: 720,
          animation: reduced ? undefined : 'notches-fade-in 320ms ease-out both',
        }}
      >
        Bring{' '}
        <em
          style={{
            fontStyle: 'italic',
            color: palette.goldDeep,
            fontWeight: 500,
          }}
        >
          structure
        </em>{' '}
        to your trading decisions.
      </h1>

      <ChoicesPrompt reduced={reduced} />

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: isMobile ? 12 : 16,
          width: '100%',
        }}
      >
        <ChoicesMicrocopyRow reduced={reduced} isMobile={isMobile} />
        {isMobile ? (
          <ChoicesVertical reduced={reduced} onSelect={onSelect} />
        ) : (
          <ChoicesHorizontal reduced={reduced} onSelect={onSelect} />
        )}
      </div>

      <LandingSecondaryLinks reduced={reduced} isMobile={isMobile} />
    </div>
  );
};

// Three sentences from the top-level nodes' `microcopy` field, laid out
// in the same 3-column grid the circles use so each sentence sits
// directly above its stage. On mobile the columns stack into a compact
// centered block, still preserving the 01/02/03 order.
const ChoicesMicrocopyRow: React.FC<{
  reduced: boolean;
  isMobile: boolean;
}> = ({ reduced, isMobile }) => {
  const sentences = entranceTree.map((n) => n.microcopy).filter(Boolean) as string[];
  if (sentences.length === 0) return null;

  if (isMobile) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 4,
          fontFamily: typeTokens.sans,
          fontSize: 13,
          color: palette.ink3,
          fontStyle: 'italic',
          lineHeight: 1.5,
          textAlign: 'center',
          maxWidth: 380,
          animation: reduced
            ? undefined
            : 'notches-fade-in 380ms ease-out both',
        }}
      >
        {sentences.map((s) => (
          <span key={s}>{s}</span>
        ))}
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        columnGap: 24,
        width: '100%',
        maxWidth: 780,
        animation: reduced
          ? undefined
          : 'notches-fade-in 380ms ease-out both',
      }}
    >
      {sentences.map((s) => (
        <span
          key={s}
          style={{
            fontFamily: typeTokens.sans,
            fontSize: 13,
            color: palette.ink3,
            fontStyle: 'italic',
            lineHeight: 1.5,
            textAlign: 'center',
            textWrap: 'balance' as any,
            padding: '0 12px',
          }}
        >
          {s}
        </span>
      ))}
    </div>
  );
};

// Small directional anchor between the supporting copy and the three
// circles — gives the reader a beat that says "these three below are the
// entry points" without turning it into a heavy label.
const ChoicesPrompt: React.FC<{ reduced: boolean }> = ({ reduced }) => (
  <span
    aria-hidden="true"
    style={{
      fontFamily: typeTokens.sans,
      fontSize: 11,
      fontWeight: 600,
      color: palette.ink3,
      letterSpacing: '0.28em',
      textTransform: 'uppercase',
      textAlign: 'center',
      animation: reduced
        ? undefined
        : 'notches-fade-in 400ms ease-out 400ms both',
      opacity: reduced ? 1 : 0,
    }}
  >
    Choose where to start
  </span>
);

// ─── Landing secondary invitations — Framework + Boring Bucket ──────────
//
// Two matched quiet callout cards. Side-by-side on desktop, stacked on
// mobile. Present enough to invite exploration without competing with the
// three-node choice above. Gold hover state on border and headline; no
// pop, no CTA button styling, no pricing.

const LandingSecondaryLinks: React.FC<{
  reduced: boolean;
  isMobile: boolean;
}> = ({ reduced, isMobile }) => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
      gap: isMobile ? 14 : 20,
      width: '100%',
      maxWidth: 720,
      marginTop: isMobile ? 8 : 16,
      animation: reduced
        ? undefined
        : 'notches-fade-in 500ms ease-out 500ms both',
      opacity: reduced ? 1 : 0,
    }}
  >
    <SecondaryCallout
      to="/n2/framework"
      eyebrow="The Notches Framework"
      headline="How the framework works"
      body="A connected process across Understand, Evaluate, and Execute."
    />
    <SecondaryCallout
      to="/n2/boring-bucket"
      eyebrow="With guidance"
      headline="Explore The Boring Bucket"
      body="Learn the framework alongside the founder — live-account view, mentoring, capital plan."
      accent
    />
  </div>
);

const SecondaryCallout: React.FC<{
  to: string;
  eyebrow: string;
  headline: string;
  body: string;
  accent?: boolean;
}> = ({ to, eyebrow, headline, body, accent }) => {
  const [hover, setHover] = useState(false);
  const [focused, setFocused] = useState(false);
  const active = hover || focused;
  return (
    <Link
      to={to}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: '18px 20px 20px',
        border: `1px solid ${active ? palette.gold : palette.line}`,
        background: accent ? palette.paper2 : palette.paper,
        borderRadius: 2,
        textDecoration: 'none',
        transition: 'border-color 180ms ease-out, background 180ms ease-out',
      }}
    >
      <span
        style={{
          fontFamily: typeTokens.sans,
          fontSize: 11,
          fontWeight: 700,
          color: accent ? palette.goldDeep : palette.ink3,
          letterSpacing: '0.2em',
          textTransform: 'uppercase',
        }}
      >
        {eyebrow}
      </span>
      <span
        style={{
          fontFamily: typeTokens.serif,
          fontSize: 20,
          fontWeight: 400,
          color: active ? palette.goldDeep : palette.ink,
          lineHeight: 1.25,
          letterSpacing: '-0.005em',
          transition: 'color 180ms ease-out',
        }}
      >
        {headline}
      </span>
      <span
        style={{
          fontFamily: typeTokens.sans,
          fontSize: 13,
          color: palette.ink2,
          lineHeight: 1.5,
        }}
      >
        {body}
      </span>
      <span
        aria-hidden="true"
        style={{
          fontFamily: typeTokens.sans,
          fontSize: 13,
          fontWeight: 600,
          color: active ? palette.goldDeep : palette.ink2,
          marginTop: 4,
          transition: 'color 180ms ease-out',
        }}
      >
        {accent ? 'Explore →' : 'Read →'}
      </span>
    </Link>
  );
};

const BrandBlock: React.FC<{ reduced: boolean }> = ({ reduced }) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 6,
      animation: reduced ? undefined : 'notches-fade-in 300ms ease-out both',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
      <span
        style={{
          fontFamily: typeTokens.serif,
          fontSize: 36,
          fontWeight: 400,
          color: palette.ink,
          letterSpacing: '-0.015em',
          lineHeight: 1,
        }}
      >
        Notches
      </span>
      <span
        style={{
          fontFamily: typeTokens.sans,
          fontSize: 11,
          fontWeight: 700,
          color: palette.ink3,
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
        }}
      >
        by WFS
      </span>
    </div>
    <div
      style={{
        fontFamily: typeTokens.sans,
        fontSize: 11,
        fontWeight: 600,
        color: palette.ink3,
        letterSpacing: '0.28em',
        textTransform: 'uppercase',
        marginTop: 4,
      }}
    >
      The Research and Trading Platform
    </div>
  </div>
);

// ─── Level 1 choices — horizontal (desktop) ──────────────────────────────

const NODE_DIAMETER = 56;
const CHOICE_MIN_WIDTH = 200;
const CONNECTOR_STROKE = 1;

const ChoicesHorizontal: React.FC<{
  reduced: boolean;
  onSelect: (node: EntranceNode) => void;
}> = ({ reduced, onSelect }) => {
  // The layout is a 3-column grid; each column holds the node + label +
  // subtitle. Connectors sit in an absolutely-positioned SVG overlay
  // (aria-hidden). All connector geometry is driven from CSS so it stays in
  // sync with the grid at any container width.
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [drawn, setDrawn] = useState<boolean>(reduced); // reduced motion → skip line draw

  useEffect(() => {
    if (reduced) {
      setDrawn(true);
      return;
    }
    // Draw connectors after the choice-enter stagger has completed (~700ms).
    const t = window.setTimeout(() => setDrawn(true), 620);
    return () => window.clearTimeout(t);
  }, [reduced]);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        width: '100%',
        maxWidth: 780,
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        columnGap: 24,
        alignItems: 'start',
      }}
    >
      <ConnectorOverlay drawn={drawn} reduced={reduced} />
      {entranceTree.map((node, idx) => (
        <NodeChoiceHorizontal
          key={node.id}
          node={node}
          index={idx}
          reduced={reduced}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
};

const ConnectorOverlay: React.FC<{ drawn: boolean; reduced: boolean }> = ({
  drawn,
  reduced,
}) => {
  // The overlay draws two thin gold lines between the three circle edges,
  // with small arrowheads. Because the grid is uniform we can position the
  // lines with % arithmetic: each column is 1/3 wide, so the circle centers
  // sit at ~16.67%, 50%, and 83.33% of the container width.
  const stroke = palette.gold;
  return (
    <svg
      aria-hidden="true"
      width="100%"
      height={NODE_DIAMETER}
      viewBox={`0 0 100 ${NODE_DIAMETER}`}
      preserveAspectRatio="none"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: NODE_DIAMETER,
        pointerEvents: 'none',
      }}
    >
      <defs>
        <marker
          id="notches-arrow"
          viewBox="0 0 10 10"
          refX="6"
          refY="5"
          markerWidth="6"
          markerHeight="6"
          orient="auto-start-reverse"
        >
          <path d="M0,0 L8,5 L0,10 z" fill={stroke} />
        </marker>
      </defs>
      {/* left-to-middle and middle-to-right; expressed in viewBox units */}
      <line
        x1={16.67 + 6}
        x2={50 - 6}
        y1={NODE_DIAMETER / 2}
        y2={NODE_DIAMETER / 2}
        stroke={stroke}
        strokeWidth={CONNECTOR_STROKE}
        markerEnd="url(#notches-arrow)"
        style={{
          strokeDasharray: reduced ? undefined : '200',
          strokeDashoffset: reduced ? 0 : drawn ? 0 : 200,
          transition: reduced
            ? undefined
            : 'stroke-dashoffset 520ms ease-out',
        }}
        // aria-hidden covered by the parent
        vectorEffect="non-scaling-stroke"
      />
      <line
        x1={50 + 6}
        x2={83.33 - 6}
        y1={NODE_DIAMETER / 2}
        y2={NODE_DIAMETER / 2}
        stroke={stroke}
        strokeWidth={CONNECTOR_STROKE}
        markerEnd="url(#notches-arrow)"
        style={{
          strokeDasharray: reduced ? undefined : '200',
          strokeDashoffset: reduced ? 0 : drawn ? 0 : 200,
          transition: reduced
            ? undefined
            : 'stroke-dashoffset 520ms ease-out 120ms',
        }}
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
};

const NodeChoiceHorizontal: React.FC<{
  node: EntranceNode;
  index: number;
  reduced: boolean;
  onSelect: (n: EntranceNode) => void;
}> = ({ node, index, reduced, onSelect }) => {
  const [hover, setHover] = useState(false);
  const [focused, setFocused] = useState(false);
  return (
    <button
      type="button"
      data-entrance-choice-id={node.id}
      onClick={() => onSelect(node)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        appearance: 'none',
        background: 'transparent',
        border: 'none',
        padding: 0,
        margin: 0,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 20,
        color: palette.ink,
        minWidth: CHOICE_MIN_WIDTH,
        textAlign: 'center',
        opacity: reduced ? 1 : 0,
        transform: reduced ? 'translateY(0)' : 'translateY(8px)',
        animation: reduced
          ? undefined
          : `notches-choice-in 360ms ease-out ${180 + index * 70}ms forwards`,
        // Visible focus ring, not dependent on color changes.
        outline: focused ? `2px solid ${palette.gold}` : 'none',
        outlineOffset: 4,
        borderRadius: 4,
      }}
    >
      <NodeCircle
        numeral={node.numeral || String(index + 1).padStart(2, '0')}
        hover={hover || focused}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
        <span
          style={{
            fontFamily: typeTokens.serif,
            fontSize: 27,
            fontWeight: 400,
            color: hover || focused ? palette.goldDeep : palette.ink,
            transition: 'color 180ms ease-out',
            letterSpacing: '-0.005em',
            lineHeight: 1.1,
          }}
        >
          {node.label}
        </span>
        {node.subtitle && (
          <span
            style={{
              fontFamily: typeTokens.sans,
              fontSize: 14,
              fontWeight: 400,
              color: palette.ink2,
              lineHeight: 1.35,
              textWrap: 'balance' as any,
              maxWidth: 220,
            }}
          >
            {node.subtitle}
          </span>
        )}
      </div>
    </button>
  );
};

const NodeCircle: React.FC<{ numeral: string; hover: boolean }> = ({
  numeral,
  hover,
}) => (
  <span
    aria-hidden="true"
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: NODE_DIAMETER,
      height: NODE_DIAMETER,
      borderRadius: '50%',
      border: `1.25px solid ${palette.gold}`,
      background: hover ? palette.goldDeep : palette.paper,
      color: hover ? palette.paper : palette.goldDeep,
      fontFamily: typeTokens.sans,
      fontSize: 14,
      fontWeight: 600,
      letterSpacing: '0.05em',
      transition:
        'background 180ms ease-out, color 180ms ease-out, transform 180ms ease-out',
      transform: hover ? 'translateY(-2px)' : 'translateY(0)',
    }}
  >
    {numeral}
  </span>
);

// ─── Level 1 choices — vertical (mobile) ─────────────────────────────────

const ChoicesVertical: React.FC<{
  reduced: boolean;
  onSelect: (node: EntranceNode) => void;
}> = ({ reduced, onSelect }) => {
  return (
    <div
      style={{
        width: '100%',
        maxWidth: 420,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {entranceTree.map((node, idx) => (
        <React.Fragment key={node.id}>
          <NodeChoiceVertical
            node={node}
            index={idx}
            reduced={reduced}
            onSelect={onSelect}
          />
          {idx < entranceTree.length - 1 && (
            <VerticalConnector reduced={reduced} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

const NodeChoiceVertical: React.FC<{
  node: EntranceNode;
  index: number;
  reduced: boolean;
  onSelect: (n: EntranceNode) => void;
}> = ({ node, index, reduced, onSelect }) => {
  const [hover, setHover] = useState(false);
  const [focused, setFocused] = useState(false);
  return (
    <button
      type="button"
      data-entrance-choice-id={node.id}
      onClick={() => onSelect(node)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        appearance: 'none',
        background: 'transparent',
        border: 'none',
        padding: 12,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        color: palette.ink,
        textAlign: 'left',
        minHeight: 68,
        opacity: reduced ? 1 : 0,
        transform: reduced ? 'translateY(0)' : 'translateY(6px)',
        animation: reduced
          ? undefined
          : `notches-choice-in 360ms ease-out ${180 + index * 70}ms forwards`,
        outline: focused ? `2px solid ${palette.gold}` : 'none',
        outlineOffset: 2,
        borderRadius: 6,
      }}
    >
      <NodeCircle
        numeral={node.numeral || String(index + 1).padStart(2, '0')}
        hover={hover || focused}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span
          style={{
            fontFamily: typeTokens.serif,
            fontSize: 24,
            fontWeight: 400,
            color: hover || focused ? palette.goldDeep : palette.ink,
            transition: 'color 180ms ease-out',
            lineHeight: 1.1,
            letterSpacing: '-0.005em',
          }}
        >
          {node.label}
        </span>
        {node.subtitle && (
          <span
            style={{
              fontFamily: typeTokens.sans,
              fontSize: 13,
              color: palette.ink2,
              lineHeight: 1.35,
            }}
          >
            {node.subtitle}
          </span>
        )}
      </div>
    </button>
  );
};

const VerticalConnector: React.FC<{ reduced: boolean }> = ({ reduced }) => (
  <div
    aria-hidden="true"
    style={{
      alignSelf: 'flex-start',
      marginLeft: 12 + NODE_DIAMETER / 2 - 0.5,
      width: 1,
      height: 20,
      background: palette.gold,
      opacity: reduced ? 1 : 0.9,
    }}
  />
);

// ─── Level 2 / 3 — the branch list ───────────────────────────────────────

const LevelBranch: React.FC<{
  headingRef: React.RefObject<HTMLHeadingElement | null>;
  reduced: boolean;
  trail: EntranceNode[];
  onSelect: (node: EntranceNode) => void;
  onNavigateToTrail: (t: EntranceNode[]) => void;
}> = ({ headingRef, reduced, trail, onSelect, onNavigateToTrail }) => {
  const current = trail[trail.length - 1];
  const parentTrail = trail.slice(0, -1);
  const children = current.children;

  const listNodes: EntranceNode[] = children ?? [];

  // A bundle-flagged node renders a plan overview instead of a child list.
  // Any branch under Evaluate — the bundle overviews and the Evaluate
  // landing itself — carries an "Explore All Research" secondary link.
  const isBundleView = Boolean(current.bundleKey);
  const isUnderEvaluate = trail.some((n) => n.id === 'evaluate');

  return (
    <div
      style={{
        width: '100%',
        maxWidth: 640,
        display: 'flex',
        flexDirection: 'column',
        gap: 40,
      }}
    >
      <BrandInline reduced={reduced} />

      <Breadcrumbs
        trail={trail}
        onNavigateToTrail={onNavigateToTrail}
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <h1
          ref={headingRef}
          tabIndex={-1}
          style={{
            fontFamily: typeTokens.serif,
            fontSize: 34,
            fontWeight: 400,
            margin: 0,
            color: palette.ink,
            letterSpacing: '-0.01em',
            textWrap: 'balance' as any,
            outline: 'none',
            animation: reduced ? undefined : 'notches-fade-in 260ms ease-out both',
          }}
        >
          {current.subtitle && (
            <span
              style={{
                display: 'block',
                fontFamily: typeTokens.sans,
                fontSize: 12,
                fontWeight: 600,
                color: palette.ink3,
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                marginBottom: 12,
              }}
            >
              {current.subtitle}
            </span>
          )}
          {current.headline || current.label}
        </h1>
        {current.description && (
          <p
            style={{
              fontFamily: typeTokens.sans,
              fontSize: 15,
              color: palette.ink2,
              lineHeight: 1.5,
              margin: 0,
              maxWidth: 560,
            }}
          >
            {current.description}
          </p>
        )}
      </div>

      {isBundleView ? (
        <BundleOverview node={current} reduced={reduced} />
      ) : (
        <ChoiceList
          nodes={listNodes}
          onSelect={onSelect}
          reduced={reduced}
        />
      )}

      {isUnderEvaluate && <AllResearchSecondary />}

      <BackButton
        onClick={() => onNavigateToTrail(parentTrail)}
        label={parentTrail.length === 0 ? 'Back to start' : `Back to ${parentTrail[parentTrail.length - 1].label}`}
      />
    </div>
  );
};

// ─── Bundle overview (Evaluate → <bundle>) ─────────────────────────────
// Renders the approved bundle-based plan card. Coverage is displayed as
// text/chips, not links, so unauthenticated visitors aren't routed into
// legacy single-symbol purchase flows. Individual research boards remain
// reachable via their own URLs for entitled and legacy customers.

// Per-secondary callout copy. Each single-market standalone plan sits
// beside a bundle that already includes it — the callout labels the
// overlap so nobody buys both. Keep the phrasing symmetric across
// standalones so the pattern reads the same on every bundle branch.
const SECONDARY_CALLOUTS: Record<
  NonNullable<EntranceNode['bundleSecondaryKey']>,
  { eyebrow: string; note: string }
> = {
  'spx-only': {
    eyebrow: 'Only need SPX?',
    note:
      'SPX is already included in Index & ETF Research and All Research. You do not need both subscriptions.',
  },
  'es-only': {
    eyebrow: 'Only need ES?',
    note:
      'ES is already included in Futures Options Research and All Research. You do not need both subscriptions.',
  },
};

const BundleOverview: React.FC<{
  node: EntranceNode;
  reduced: boolean;
}> = ({ node, reduced }) => {
  const { user } = useAuth();
  if (!node.bundleKey) return null;
  const plan = getPlan(node.bundleKey);
  const secondary = node.bundleSecondaryKey ? getPlan(node.bundleSecondaryKey) : null;
  const secondaryCopy = node.bundleSecondaryKey ? SECONDARY_CALLOUTS[node.bundleSecondaryKey] : null;

  // Which markets the current visitor is already entitled to. Only these
  // render as "Open Research" links so we never route someone into a
  // board they can't yet use.
  const accessibleMarkets = (plan.markets || []).filter((m) => {
    if (m === 'Premier') {
      return !!user && ((user as any).effectiveTier === 'premier' || (user as any).effectiveTier === 'pro');
    }
    if (SYMBOL_MARKETS.has(m)) {
      return canAccessSymbol(user as any, m as SymbolName);
    }
    return false;
  });
  const showOpenResearch = accessibleMarkets.length > 0;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
        animation: reduced ? undefined : 'notches-fade-in 220ms ease-out both',
      }}
    >
      <BundleCard plan={plan} primary />
      {showOpenResearch && (
        <OpenResearchPanel markets={accessibleMarkets} />
      )}
      {secondary && secondaryCopy && (
        <SecondaryPlanCallout
          plan={secondary}
          eyebrow={secondaryCopy.eyebrow}
          note={secondaryCopy.note}
        />
      )}
    </div>
  );
};

// Signed-in visitors with existing access see direct links to the
// boards they own. Board URLs remain reachable via bookmarks for
// everyone else, but the wizard only surfaces them when access is
// confirmed — the wizard never implies that reaching a board URL
// establishes paid access.
const OpenResearchPanel: React.FC<{ markets: string[] }> = ({ markets }) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
      border: `1px solid ${palette.line}`,
      background: palette.paper,
      borderRadius: 2,
      padding: 20,
    }}
  >
    <div>
      <span
        style={{
          fontFamily: typeTokens.sans,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.2em',
          textTransform: 'uppercase',
          color: palette.ink3,
        }}
      >
        Your access
      </span>
      <p
        style={{
          fontFamily: typeTokens.sans,
          fontSize: 14,
          color: palette.ink2,
          lineHeight: 1.55,
          margin: '6px 0 0',
        }}
      >
        You already have research access for the boards below.
      </p>
    </div>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
      {markets.map((m) => (
        <Link
          key={m}
          to={MARKET_BOARDS[m] || '/'}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 14px',
            borderRadius: 2,
            border: `1px solid ${palette.gold}`,
            background: palette.paper2,
            color: palette.ink,
            fontFamily: typeTokens.sans,
            fontSize: 13,
            fontWeight: 600,
            textDecoration: 'none',
          }}
        >
          Open {m} Research →
        </Link>
      ))}
    </div>
  </div>
);

const BundleCard: React.FC<{ plan: ResearchPlan; primary?: boolean }> = ({
  plan,
  primary,
}) => (
  <div
    style={{
      border: `1px solid ${primary ? palette.gold : palette.line}`,
      background: primary ? palette.paper2 : palette.paper,
      borderRadius: 2,
      padding: 24,
      display: 'flex',
      flexDirection: 'column',
      gap: 14,
    }}
  >
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 20,
        flexWrap: 'wrap',
      }}
    >
      <h2
        style={{
          fontFamily: typeTokens.serif,
          fontSize: 24,
          fontWeight: 400,
          margin: 0,
          color: palette.ink,
          lineHeight: 1.2,
          letterSpacing: '-0.005em',
        }}
      >
        {plan.name}
      </h2>
      <PriceLabel plan={plan} />
    </div>

    <p
      style={{
        fontFamily: typeTokens.sans,
        fontSize: 15,
        color: palette.ink2,
        lineHeight: 1.55,
        margin: 0,
        maxWidth: 560,
      }}
    >
      {plan.description}
    </p>

    {plan.markets.length > 0 && <MarketChips markets={plan.markets} />}

    <ul
      style={{
        listStyle: 'disc',
        paddingLeft: 20,
        margin: 0,
        color: palette.ink2,
        fontFamily: typeTokens.sans,
        fontSize: 14,
        lineHeight: 1.55,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      {plan.bullets.map((b) => (
        <li key={b}>{b}</li>
      ))}
    </ul>

    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 4 }}>
      <BundlePrimaryCta plan={plan} />
    </div>
  </div>
);

const SecondaryPlanCallout: React.FC<{
  plan: ResearchPlan;
  eyebrow: string;
  note: string;
}> = ({ plan, eyebrow, note }) => (
  <div
    style={{
      border: `1px dashed ${palette.line}`,
      borderRadius: 2,
      padding: 20,
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
      background: palette.paper,
    }}
  >
    <span
      style={{
        fontFamily: typeTokens.sans,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.2em',
        textTransform: 'uppercase',
        color: palette.ink3,
      }}
    >
      {eyebrow}
    </span>
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 16,
        flexWrap: 'wrap',
      }}
    >
      <div
        style={{
          fontFamily: typeTokens.serif,
          fontSize: 18,
          color: palette.ink,
          lineHeight: 1.25,
        }}
      >
        {plan.name}
      </div>
      <PriceLabel plan={plan} small />
    </div>
    <p
      style={{
        fontFamily: typeTokens.sans,
        fontSize: 13,
        color: palette.ink3,
        lineHeight: 1.55,
        margin: 0,
        maxWidth: 560,
      }}
    >
      {note}
    </p>
    <BundlePrimaryCta plan={plan} small />
  </div>
);

const PriceLabel: React.FC<{ plan: ResearchPlan; small?: boolean }> = ({
  plan,
  small,
}) => (
  <div
    style={{
      fontFamily: typeTokens.sans,
      fontSize: small ? 12 : 13,
      color: palette.ink2,
      textAlign: 'right',
      minWidth: 140,
    }}
  >
    <div style={{ color: palette.ink, fontSize: small ? 14 : 16 }}>
      <strong>${plan.monthlyPrice.toFixed(2)}</strong>
      <span style={{ fontSize: small ? 11 : 12, color: palette.ink3 }}>
        {' '}/ month
      </span>
    </div>
    {plan.yearlyPrice != null && (
      <div style={{ color: palette.ink3, fontSize: small ? 11 : 12, marginTop: 2 }}>
        or ${plan.yearlyPrice.toFixed(2)} / year
      </div>
    )}
  </div>
);

const MarketChips: React.FC<{ markets: string[] }> = ({ markets }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    <span
      style={{
        fontFamily: typeTokens.sans,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.2em',
        textTransform: 'uppercase',
        color: palette.ink3,
      }}
    >
      Included markets
    </span>
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {markets.map((m) => (
        <span
          key={m}
          style={{
            fontFamily: typeTokens.sans,
            fontSize: 12,
            fontWeight: 600,
            color: palette.ink2,
            background: palette.paper,
            border: `1px solid ${palette.line}`,
            borderRadius: 2,
            padding: '4px 10px',
            letterSpacing: '0.04em',
          }}
        >
          {m}
        </span>
      ))}
    </div>
  </div>
);

const BundlePrimaryCta: React.FC<{ plan: ResearchPlan; small?: boolean }> = ({
  plan,
  small,
}) => {
  const label =
    plan.purchase === 'ready'
      ? 'View Plan →'
      : plan.purchase === 'application'
      ? 'Explore the mentored membership →'
      : 'View Plan →';
  return (
    <Link
      to={plansPagePath(plan.key)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: small ? '8px 14px' : '10px 16px',
        borderRadius: 2,
        textDecoration: 'none',
        fontFamily: typeTokens.sans,
        fontSize: small ? 13 : 14,
        fontWeight: 600,
        color: palette.paper,
        background: palette.goldDeep,
        border: `1px solid ${palette.goldDeep}`,
      }}
    >
      {label}
    </Link>
  );
};

const AllResearchSecondary: React.FC = () => (
  <div
    style={{
      display: 'flex',
      alignItems: 'baseline',
      gap: 8,
      fontFamily: typeTokens.sans,
      fontSize: 14,
      color: palette.ink2,
    }}
  >
    <span style={{ color: palette.ink3 }}>Want everything in one subscription?</span>
    <Link
      to={plansPagePath('all-research')}
      style={{
        color: palette.goldDeep,
        textDecoration: 'underline',
        textDecorationColor: palette.line,
        textUnderlineOffset: 3,
        fontWeight: 600,
      }}
    >
      Explore All Research →
    </Link>
  </div>
);

const BrandInline: React.FC<{ reduced: boolean }> = ({ reduced }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'baseline',
      gap: 10,
      animation: reduced ? undefined : 'notches-fade-in 220ms ease-out both',
    }}
  >
    <Link
      to="/n2"
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 10,
        textDecoration: 'none',
      }}
    >
      <span
        style={{
          fontFamily: typeTokens.serif,
          fontSize: 24,
          fontWeight: 400,
          color: palette.ink,
          letterSpacing: '-0.015em',
          lineHeight: 1,
        }}
      >
        Notches
      </span>
      <span
        style={{
          fontFamily: typeTokens.sans,
          fontSize: 10,
          fontWeight: 700,
          color: palette.ink3,
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
        }}
      >
        by WFS
      </span>
    </Link>
  </div>
);

const Breadcrumbs: React.FC<{
  trail: EntranceNode[];
  onNavigateToTrail: (t: EntranceNode[]) => void;
}> = ({ trail, onNavigateToTrail }) => (
  <nav
    aria-label="Breadcrumb"
    style={{ fontFamily: typeTokens.sans, fontSize: 13, color: palette.ink3 }}
  >
    <ol
      style={{
        listStyle: 'none',
        padding: 0,
        margin: 0,
        display: 'flex',
        gap: 8,
        flexWrap: 'wrap',
      }}
    >
      <li>
        <button
          type="button"
          onClick={() => onNavigateToTrail([])}
          style={crumbLinkStyle(false)}
        >
          Notches
        </button>
      </li>
      {trail.map((node, i) => {
        const isLast = i === trail.length - 1;
        return (
          <React.Fragment key={node.id}>
            <li aria-hidden="true" style={{ color: palette.ink3 }}>
              /
            </li>
            <li>
              {isLast ? (
                <span style={{ ...crumbLinkStyle(true), color: palette.ink }}>
                  {node.label}
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => onNavigateToTrail(trail.slice(0, i + 1))}
                  style={crumbLinkStyle(false)}
                >
                  {node.label}
                </button>
              )}
            </li>
          </React.Fragment>
        );
      })}
    </ol>
  </nav>
);

function crumbLinkStyle(active: boolean): React.CSSProperties {
  return {
    appearance: 'none',
    background: 'transparent',
    border: 'none',
    padding: 0,
    cursor: active ? 'default' : 'pointer',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    fontWeight: active ? 600 : 500,
    color: active ? palette.ink : palette.ink2,
    textDecoration: active ? 'none' : 'underline',
    textDecorationColor: palette.line,
    textUnderlineOffset: 3,
  };
}

const ChoiceList: React.FC<{
  nodes: EntranceNode[];
  onSelect: (n: EntranceNode) => void;
  reduced: boolean;
}> = ({ nodes, onSelect, reduced }) =>
  nodes.length === 0 ? (
    <p style={{ color: palette.ink2 }}>No choices available.</p>
  ) : (
    <ul
      style={{
        listStyle: 'none',
        padding: 0,
        margin: 0,
        borderTop: `1px solid ${palette.line}`,
      }}
    >
      {nodes.map((node, i) => (
        <ChoiceRow
          key={node.id}
          node={node}
          index={i}
          onSelect={onSelect}
          reduced={reduced}
        />
      ))}
    </ul>
  );

const ChoiceRow: React.FC<{
  node: EntranceNode;
  index: number;
  onSelect: (n: EntranceNode) => void;
  reduced: boolean;
}> = ({ node, index, onSelect, reduced }) => {
  const [hover, setHover] = useState(false);
  const [focused, setFocused] = useState(false);
  const hasChildren = !!node.children && node.children.length > 0;
  // A bundle-flagged node advances the wizard into a bundle overview even
  // though it has no children of its own. Treat it as an intermediate step
  // — click fires onSelect and pushes it onto the trail.
  const advancesWizard = hasChildren || !!node.bundleKey;
  const flagged = !!node.flag;
  const destination = flagged ? undefined : node.destination;
  const label = node.destinationLabel || (advancesWizard ? node.label : `${node.label} →`);

  const commonRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 20,
    padding: '18px 12px',
    borderBottom: `1px solid ${palette.line}`,
    background: hover || focused ? palette.paper2 : 'transparent',
    transition: 'background 150ms ease-out',
    cursor: flagged ? 'not-allowed' : 'pointer',
    color: palette.ink,
    textDecoration: 'none',
    fontFamily: typeTokens.sans,
    opacity: reduced ? 1 : 0,
    transform: reduced ? 'translateY(0)' : 'translateY(6px)',
    animation: reduced
      ? undefined
      : `notches-choice-in 320ms ease-out ${60 + index * 60}ms forwards`,
    outline: focused ? `2px solid ${palette.gold}` : 'none',
    outlineOffset: -2,
    borderRadius: 2,
  };

  const inner = (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
        <span
          style={{
            fontFamily: typeTokens.serif,
            fontSize: 20,
            fontWeight: 400,
            color: hover || focused ? palette.goldDeep : palette.ink,
            transition: 'color 150ms ease-out',
            lineHeight: 1.2,
          }}
        >
          {node.label}
          {flagged && (
            <span
              style={{
                marginLeft: 10,
                fontFamily: typeTokens.sans,
                fontSize: 11,
                fontWeight: 700,
                color: palette.caution,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
              }}
            >
              review
            </span>
          )}
        </span>
        {node.description && (
          <span
            style={{
              fontFamily: typeTokens.sans,
              fontSize: 14,
              color: palette.ink2,
              lineHeight: 1.4,
              maxWidth: 460,
            }}
          >
            {node.description}
          </span>
        )}
        {flagged && (
          <span
            style={{
              fontFamily: typeTokens.sans,
              fontSize: 12,
              color: palette.caution,
              marginTop: 2,
            }}
          >
            {node.flag}
          </span>
        )}
      </div>
      <span
        aria-hidden="true"
        style={{
          fontFamily: typeTokens.sans,
          fontSize: 15,
          color: hover || focused ? palette.goldDeep : palette.ink3,
          whiteSpace: 'nowrap',
          transition: 'color 150ms ease-out',
        }}
      >
        {flagged ? '' : (advancesWizard ? '→' : label.endsWith('→') ? label : `${label}`)}
      </span>
    </>
  );

  if (advancesWizard) {
    // Intermediate node — advance the wizard. Includes bundle-flagged
    // nodes that render a plan overview after selection.
    return (
      <li>
        <button
          type="button"
          data-entrance-choice-id={node.id}
          onClick={() => onSelect(node)}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            ...commonRowStyle,
            width: '100%',
            appearance: 'none',
            border: 'none',
            textAlign: 'left',
          }}
        >
          {inner}
        </button>
      </li>
    );
  }

  // Leaf — hard-link to the destination (or nothing if flagged).
  if (!destination) {
    return (
      <li>
        <div
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          style={commonRowStyle}
        >
          {inner}
        </div>
      </li>
    );
  }

  const isInternalPath = destination.startsWith('/') && !destination.startsWith('//');
  return (
    <li>
      {isInternalPath ? (
        <Link
          to={destination}
          data-entrance-choice-id={node.id}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={commonRowStyle as any}
        >
          {inner}
        </Link>
      ) : (
        <a
          href={destination}
          data-entrance-choice-id={node.id}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={commonRowStyle as any}
        >
          {inner}
        </a>
      )}
    </li>
  );
};

const BackButton: React.FC<{ onClick: () => void; label: string }> = ({
  onClick,
  label,
}) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      alignSelf: 'flex-start',
      appearance: 'none',
      background: 'transparent',
      border: 'none',
      padding: '8px 2px',
      color: palette.ink2,
      fontFamily: typeTokens.sans,
      fontSize: 13,
      fontWeight: 500,
      cursor: 'pointer',
      textDecoration: 'underline',
      textDecorationColor: palette.line,
      textUnderlineOffset: 3,
    }}
  >
    ← {label}
  </button>
);

// ─── Footer — restrained utility strip ───────────────────────────────────

const UtilityFooter: React.FC = () => {
  const { user } = useAuth();
  const loc = useLocation();
  const authRedirect = encodeURIComponent(
    (loc.pathname + loc.search).replace(/^\/+/, '')
  );
  return (
    <footer
      style={{
        padding: '28px 32px 40px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 10,
        color: palette.ink3,
        fontFamily: typeTokens.sans,
        fontSize: 12,
      }}
    >
      <div>Built by Wealth Financial Systems</div>
      <nav
        aria-label="Utility"
        style={{
          display: 'flex',
          gap: 18,
          flexWrap: 'wrap',
          justifyContent: 'center',
        }}
      >
        <Link to="/n2/about" style={utilityLinkStyle}>
          About
        </Link>
        <span aria-hidden="true">·</span>
        <Link to="/n2/methodology" style={utilityLinkStyle}>
          Methodology
        </Link>
        <span aria-hidden="true">·</span>
        {user ? (
          <Link to="/notches/dashboard" style={utilityLinkStyle}>
            Dashboard →
          </Link>
        ) : (
          <Link to={`/login?redirect=${authRedirect}`} style={utilityLinkStyle}>
            Sign in
          </Link>
        )}
      </nav>
    </footer>
  );
};

const utilityLinkStyle: React.CSSProperties = {
  color: palette.ink2,
  textDecoration: 'none',
  borderBottom: `1px solid ${palette.line}`,
  paddingBottom: 1,
  fontWeight: 500,
};

// ─── Keyframes ────────────────────────────────────────────────────────────
//
// We inject a small stylesheet once, per app-lifetime, so the entrance can
// use CSS animations without dragging in a dependency. Everything is scoped
// to .notches-entrance-root so nothing leaks out.

if (typeof document !== 'undefined' && !document.getElementById('notches-entrance-keyframes')) {
  const style = document.createElement('style');
  style.id = 'notches-entrance-keyframes';
  style.textContent = `
    @keyframes notches-fade-in {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    @keyframes notches-choice-in {
      from { opacity: 0; transform: translateY(8px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .notches-entrance-root button:focus-visible,
    .notches-entrance-root a:focus-visible {
      outline: 2px solid ${palette.gold};
      outline-offset: 2px;
    }
    @media (prefers-reduced-motion: reduce) {
      .notches-entrance-root * {
        animation: none !important;
        transition: none !important;
      }
    }
  `;
  document.head.appendChild(style);
}
